import { IContext } from "@src/context";
import { vhmwebconfig } from "@src/proto/jsonapi";
import { jsonFetch } from "@src/utils/fetch";
import { getPageCategory } from "@src/utils/helper";
import { isTimeInRange } from "@src/utils/timeInRange";
import { setThankYouPageNotice } from "@src/containers/components/ThankYou/notice";

const API_WEB_CONFIG = "/api/web_config.json";

// TODO: will change to use web config from API
const thankyouPageNotice = {
  enabled: true,
  noticeStartAt: "1765497600000", // 2025-12-12T09:00:00+09:00
  noticeEndAt: "1767571140000", // 2026-01-05T08:59:00+09:00
  notice: {
    en: "We will be closed during the New Year holiday period.\r\nNew Year Holiday Closure: December 27th, 2025 (Sat) - January 4th, 2026 (Sun)\r\nAny inquiries received during the closure period will be responded to in order from January 5th, 2026 (Mon).",
    ja: "弊社では下記の期間を年末年始休業とさせていただきます。\r\n年末年始休業: 2025年12月27日（土）～ 2026年1月4日（日）\r\n休業期間中のお問い合わせにつきましては、2026年1月5日（月）より順次対応させていただきます。",
    pt: "Entraremos em Recesso de Final de Ano.\r\nFeriado de Fim de Ano: 27/12/2025 - 04/01/2026\r\nAs consultas recebidas durante o recesso serão respondidas gradualmente a partir de 5 de janeiro de 2026.",
    vi: "Chúng tôi xin thông báo thời gian nghỉ Tết theo như dưới đây.\r\nThời gian nghỉ Tết: 27/12/2025 (Thứ 7) - 04/01/2026 (Chủ Nhật)\r\nNhững thắc mắc trong thời gian  nghỉ Tết, chúng tôi xin phép giải đáp từ ngày 05/01 (Thứ 2) theo thứ tự tiếp nhận.",
    zh: "请注意我们将会于以下期间休假\r\n年末年始休假: 2025/12/27 - 2026/1/4\r\n我们会于2026/1/4 早上09:00开始重新营业。\r\n不便之处，敬请原谅。",
    my: "ကျွန်ုပ်တို့၏ ကုမ္ပဏီသည် အောက်ဖော်ပြပါ ကာလအတွင်း နှစ်သစ်ကူး ရုံးပိတ်မည်ဖြစ်ပါသည်။\r\nနှစ်သစ်ကူး ရုံးပိတ်ရက်: 2025 ခုနှစ်၊ ဒီဇင်ဘာလ 27 ရက် (စနေနေ့) ～ 2026 ခုနှစ်၊ ဇန်နဝါရီလ 4 ရက် (တနင်္ဂနွေနေ့) အထိ\r\nရုံးပိတ်ရက်ကာလအတွင်း မေးမြန်းစုံစမ်းမှုများအတွက်မူ 2026 ခုနှစ်၊ ဇန်နဝါရီလ 5 ရက် (တနင်္လာနေ့) မှ စတင်၍ အစဉ်လိုက် ဝန်ဆောင်မှုပေးပါမည်။",
    id: "Kami libur beroperasional pada tanggal di bawah ini.\r\nLibur akhir tahun: 27/12/2025 - 4/1/2026\r\nTerkait pertanyaan pada periode tersebut, akan dibalas sesuai urutan di tanggal 5/1/2026.",
    ne: "हाम्रो कम्पनी तल उल्लेखित मितिमा वर्षान्त तथा नववर्षको बिदाका कारण बन्द रहनेछ।\r\nबिदा अवधि: 2025 डिसेम्बर 27 (शनिबार) देखि 2026 जनवरी 4 (आइतबार) सम्म\r\nबिदा अवधिमा प्राप्त भएका सम्पर्क तथा सोधपुछहरूलाई 2025 जनवरी 5 (सोमबार) देखि क्रमिक रूपमा जवाफ दिइनेछ।",
  },
};
const apartmentVisitForm = {
  datePickerDisabledDates: [
    {
      from: "2025/12/25",
      to: "2026/01/04",
    },
  ],
  hideEntranceEnabled: true,
  hideEntranceBtnStartAt: "1766732400000", // 2025-12-26T16:00:00+09:00
  hideEntranceBtnEndAt: "1767571140000", // 2026-01-05T08:59:00+09:00
};

export function setupWebConfig(context: IContext) {
  const { locale } = context;
  const pageCategory = getPageCategory();

  jsonFetch<vhmwebconfig.IWebConfig>(API_WEB_CONFIG).then((res = {}) => {
    const category = pageCategory === "parking" ? "parking" : "apartment"; // default to apartment
    // office hour text
    if (
      res.phoneNumberOfficeHourText &&
      res.phoneNumberOfficeHourText[category] &&
      res.phoneNumberOfficeHourText[category][locale]
    ) {
      setOfficeHourText(res.phoneNumberOfficeHourText[category][locale]);
    }

    // hide phone number
    if (
      res.phoneNumberInvisible &&
      res.phoneNumberInvisible[category] &&
      res.phoneNumberInvisible[category][locale]
    ) {
      hidePhoneNumber();
    }

    // thank you page notice
    setThankYouPageNotice(context, thankyouPageNotice);

    // hide book visit entrance button
    if (
      apartmentVisitForm.hideEntranceEnabled &&
      isTimeInRange(
        apartmentVisitForm.hideEntranceBtnStartAt,
        apartmentVisitForm.hideEntranceBtnEndAt
      )
    ) {
      // disable book visit entrance button
      disableBookVisitEntranceButton(true);
    } else {
      // enable book visit entrance button
      disableBookVisitEntranceButton(false);
      // disable dates in book visit date picker
      saveBookVisitDatePickerDisabledDates(
        apartmentVisitForm.datePickerDisabledDates
      );
    }
  });
}

function setOfficeHourText(text: string) {
  document
    .querySelectorAll("[data-office-hour-text]")
    .forEach((item) => (item.innerHTML = text));
}

function hidePhoneNumber() {
  document
    .querySelectorAll("[data-phone-number-display]")
    .forEach((item) => item.remove());
}

function disableBookVisitEntranceButton(disabled: boolean) {
  document
    .querySelectorAll(
      '[data-popup-id="book-visit"][data-popup-event="popup-open"]'
    )
    .forEach((item) => {
      item.removeAttribute(disabled ? "data-enabled" : "data-disabled");
      item.setAttribute(disabled ? "data-disabled" : "data-enabled", "");
    });
}

function saveBookVisitDatePickerDisabledDates(
  dates: { from: string; to: string }[]
) {
  // disabledDates: ["yyyy/mm/dd-yyyy/mm/dd", "yyyy/mm/dd"]
  const disabledDates = dates.map((item) => {
    return `${item.from}-${item.to}`;
  });
  if (disabledDates.length > 0) {
    sessionStorage?.setItem(
      "book_visit_datepicker_disabled_dates",
      JSON.stringify(disabledDates)
    );
  } else {
    sessionStorage?.removeItem("book_visit_datepicker_disabled_dates");
  }
}
